import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { SettingsHint } from '../../../components/design-system/common/SettingsPanel';

/** The longest set of rules the API keeps (SHOP_RULES_MAX in pms-api-v2 shopper.prompt.ts). */
export const ASSISTANT_RULES_MAX = 1200;

/** Starting points a vendor can add with one click; each is one line of their rules. */
const EXAMPLES = ['FORMAL', 'SUPPLIES', 'NO_DATES', 'SAMPLES'] as const;

/**
 * The shop's own rules for its shopping assistant.
 *
 * Asked for after Carrelage Signature's owner wanted the assistant to sound
 * like the shop: it already speaks as "we", and this is where each shop says
 * how — "vouvoyez toujours", "proposez nos produits de pose", "ne promettez
 * jamais de délai". Plain text, one rule a line; the examples are lines the
 * vendor adds and then edits, never hidden presets.
 */
export const AssistantRulesField = ({
    value,
    onChange,
    onFocus,
}: {
    value: string | undefined;
    onChange: (rules: string) => void;
    onFocus?: () => void;
}) => {
    const { t } = useTranslation();
    const rules = value ?? '';

    const add = (line: string) => {
        if (rules.includes(line)) return;
        const next = rules.trim() ? `${rules.trimEnd()}\n${line}` : line;
        onChange(next.slice(0, ASSISTANT_RULES_MAX));
    };

    return (
        <div className="flex flex-col gap-2">
            <Textarea
                id="chat-rules"
                rows={5}
                maxLength={ASSISTANT_RULES_MAX}
                aria-label={t('SHOPPER_UI.SETTINGS.RULES_TITLE') as string}
                value={rules}
                placeholder={t('SHOPPER_UI.SETTINGS.RULES_PLACEHOLDER') as string}
                onChange={(e) => onChange(e.target.value)}
                onFocus={onFocus}
                data-testid="chat-rules"
            />
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{t('SHOPPER_UI.SETTINGS.RULES_EXAMPLES')}</span>
                {EXAMPLES.map((key) => {
                    const line = t(`SHOPPER_UI.SETTINGS.RULES_EXAMPLE_${key}`) as string;
                    return (
                        <Button
                            key={key}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs font-normal"
                            disabled={rules.includes(line)}
                            onClick={() => add(line)}
                            data-testid={`chat-rules-example-${key.toLowerCase()}`}
                        >
                            <Plus className="size-3" aria-hidden />
                            {line}
                        </Button>
                    );
                })}
            </div>
            <SettingsHint>
                {t('SHOPPER_UI.SETTINGS.RULES_HINT', { count: rules.length, max: ASSISTANT_RULES_MAX })}
            </SettingsHint>
        </div>
    );
};
